// src/hooks/useWpnSearch.ts
import { useCallback, useMemo, useRef, useState } from "react";

export type WpnItem = {
  id: string;
  name: string;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  addressParts?: any;
};

function env(key: string): string | undefined {
  // Vite first, then process.env for safety in SSR/tools
  // @ts-ignore
  const v = (import.meta?.env && import.meta.env[key]) as string | undefined;
  return v ?? (typeof process !== "undefined" ? (process as any).env?.[key] : undefined);
}

// Browser calls Hosting-proxied URL in prod, Vite proxy in dev.
// Default to the rewrite path so we don’t need hardcoded run.app URLs.
const DEFAULT_WPN_URL = "/api/wpnSearch";

/** Small helper that tolerates different server shapes */
function normalizeItems(payload: any): WpnItem[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload as WpnItem[];
  if (Array.isArray(payload.items)) return payload.items as WpnItem[];
  if (Array.isArray(payload.results)) return payload.results as WpnItem[];
  return [];
}

export async function runWpnSearch(term: string, lat?: number | null, lng?: number | null, signal?: AbortSignal) {
  const url = (env("VITE_WPN_FUNCTION_URL") || DEFAULT_WPN_URL).replace(/\/+$/, "");
  const r = await fetch(url, {
    method: "POST",
    signal,
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ q: term, lat: lat ?? undefined, lng: lng ?? undefined }),
  });
  if (!r.ok) {
    const msg = await r.text().catch(() => "");
    throw new Error(`wpnSearch HTTP ${r.status} ${r.statusText} ${msg}`);
  }
  const payload = await r.json();
  return normalizeItems(payload);
}

export function useWpnSearch(urlFromEnv?: string) {
  const wpnUrl =
    (urlFromEnv || env("VITE_WPN_FUNCTION_URL") || DEFAULT_WPN_URL).replace(/\/+$/, "");
  const [results, setResults] = useState<WpnItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setErr] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const search = useCallback(
    async (q: string, lat?: number | null, lng?: number | null) => {
      setErr(null);
      setResults([]);
      if (!wpnUrl || !q.trim()) return [];

      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);

      try {
        const r = await fetch(wpnUrl, {
          method: "POST",
          signal: ctrl.signal,
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ q, lat: lat ?? undefined, lng: lng ?? undefined }),
        });
        if (!r.ok) throw new Error(`WPN ${r.status}`);
        const payload = await r.json();
        const items = normalizeItems(payload);
        setResults(items);
        return items;
      } catch (e: any) {
        if (e?.name !== "AbortError") setErr(e?.message || "WPN search failed");
        return [];
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    },
    [wpnUrl]
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setResults([]);
    setLoading(false);
    setErr(null);
  }, []);

  return useMemo(
    () => ({ results, loading, error, search, reset, enabled: !!wpnUrl }),
    [results, loading, error, search, reset, wpnUrl]
  );
}
